import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../components/modal/modal.component";
import InputComponent from "../../components/input/input.component";
import SwitchComponent from "../../components/switch/switch.component";
import SelectComponent from "../../components/select/select.component";
import { SystemUserService } from "../../services/system-user";
import { to } from "../../polyfill";
import { useForm } from "react-hook-form";
import Button from "../../components/button/button.component";
import { StorageService } from "../../services/storage";
import { STORAGE } from "../../constants";

function FormModal({ show, data, users, setShow }: any) {
  const account = StorageService.get(STORAGE.ACCOUNT);
  const [session] = useState(StorageService.get(STORAGE.USER))
  
  const defaultValues = {
    _id: '',
    account: '',
    name: '',
    email: '',
    role: '',
    status: true,
    permissions: {
      cortes: true,
      cuentas: true,
      gastos: true,
      prestamos: true,
      recordatorios: true,
    }
  }

  const { watch, setValue, reset, register, handleSubmit, formState: { errors, isSubmitting, isValid }, } = useForm<typeof defaultValues>({
    mode: 'onChange',
    defaultValues,
  });

  const { role } = watch();

  const totalAdmins = useMemo(() => users.filter( (l: any) => l.role === 'admin').length, [users]);
  const totalCapturists = useMemo(() => users.filter( (l: any) => l.role === 'capturist').length, [users]);

  const remainingLicenses = useMemo(() => {
    switch (account.plan) {
      case "BÁSICO":
        return {
          admin: 1 - totalAdmins,
          capturist: 2 - totalCapturists,
        }
      case "PLATA":
        return {
          admin: 2 - totalAdmins,
          capturist: 3 - totalCapturists,
        }
      case "ORO":
        return {
          admin: 2 - totalAdmins,
          capturist: 6 - totalCapturists,
        }
      case "DIAMANTE":
        return {
          admin: 5 - totalAdmins,
          capturist: 15 - totalCapturists,
        }

      default:
          return {
            admin: 0,
            capturist: 0,
          } 
    }
  }, [account, totalAdmins, totalCapturists]);

  const getDetailsPlan = () => {
    switch (account?.plan) {
      case "BÁSICO":
        return (
          <>
            <li>Un acceso web y móvil.</li>
            <li>Dos accesos solo en móvil.</li>
          </>
        );
      case "PLATA":
        return (
          <>
            <li>Dos accesos web y móvil.</li>
            <li>Tres accesos solo en móvil.</li>
          </>
        );
      case "ORO":
        return (
          <>
            <li>Dos accesos web y móvil.</li>
            <li>Seis accesos solo en móvil.</li>
          </>
        );
      case "DIAMANTE":
        return (
          <>
            <li>Cinco usuarios web y móvil.</li>
            <li>Quince accesos solo en móvil.</li>
          </>
        );
    }
  };

  
  const submit = async(form: typeof defaultValues) => {
    const [error, data] = await to(SystemUserService.save(form));
    if(error) {
      alert(error.message)
    } else {
      setShow(true)
    }
  }

  useEffect(() => {
    if(data) {
      setValue('_id', data._id || '');
      setValue('name', data.name);
      setValue('email', data.email);
      setValue('role', data.role);
      setValue('status', data.status);
      setValue('permissions', data.permissions);
    } else {
      reset(defaultValues);
    }
  }, [data, show])

  
  return (
    <Modal show={show}>
      <div className="modal-header">
        <h3 className="modal-title">{watch('_id') ? 'Editar': 'Agregar'} usuario de sistema</h3>
        <div
          className="btn btn-icon btn-sm btn-active-light-primary ms-2"
          onClick={setShow}
        >
          <i className="ki-duotone ki-cross fs-1">
            <span className="path1" />
            <span className="path2" />
          </i>
        </div>
      </div>
      <div className="modal-body">
        <div className="mb-5">
          <InputComponent label="Nombre *" {...register('name', { required: true })} errors={errors.name} />
        </div>
        
        <div className="mb-5">
          <InputComponent label="Correo *" {...register('email', { required: true })} errors={errors.email} />
        </div>
        
        <div className="alert bg-light-warning border border-warning border-dashed d-flex flex-column flex-sm-row w-100 p-5 ">
          <i className="fa-solid fa-circle-exclamation fs-2hx text-warning me-4 mb-5 mb-sm-0"></i>
          <div className="d-flex flex-column">
            <h5 className="mb-1">¡Atención!</h5>
            <span>
              Actualmente estás utilizando el plan{" "}
              <b>{account?.plan}</b>, el cual incluye:
              <ul>{getDetailsPlan()}</ul>
              Uso actual:
              <ul>
                <li>{totalAdmins} acceso web y móvil utilizado.</li>
                <li>{totalCapturists} accesos móviles utilizados.</li>
              </ul>
              ¿Necesitas más licencias? Ponte en contacto con tu
              agente de ventas.
            </span>
          </div>
        </div>
        
        <div className="mb-5">
          <SelectComponent 
            label="Rol *" 
            {...register('role', { required: true })} errors={errors.role}
            data={ [
            {
                value: 'admin',
                text: 'Administrador',
                disabled: remainingLicenses.admin === 0
            },
            {
                value: 'capturist',
                text: 'Capturista (acceso móvil)',
                disabled: remainingLicenses.capturist === 0
            },
          ] }/>
        </div>

        {
          role === 'capturist' && (
            <div>
              <h6 className="mb-4">Permisos</h6>
              <div className="mb-4"><SwitchComponent label="Cortes" {...register('permissions.cortes')} errors={errors.permissions?.cortes} /></div>
              <div className="mb-4"><SwitchComponent label="Cuentas de socios" {...register('permissions.cuentas')} errors={errors.permissions?.cuentas} /></div>
              <div className="mb-4"><SwitchComponent label="Gastos y compras" {...register('permissions.gastos')} errors={errors.permissions?.gastos} /></div>
              <div className="mb-4"><SwitchComponent label="Préstamos y Créditos" {...register('permissions.prestamos')} errors={errors.permissions?.prestamos} /></div>
              <div className="mb-4"><SwitchComponent label="Recordatorios" {...register('permissions.recordatorios')} errors={errors.permissions?.recordatorios} /></div>

              <hr />
            </div>
          )
        }
        {
          watch('_id') && watch('_id') !== session._id && <SwitchComponent label="Estatus" {...register('status')} errors={errors.status} />
        }
      </div>
      <div className="modal-footer">
        <button className="btn btn-light" type="button" onClick={setShow}>
          Cancelar
        </button>
        <Button className="btn btn-primary" disabled={!isValid} isLoading={isSubmitting} onClick={handleSubmit(submit)}>
          Guardar
        </Button>
      </div>
    </Modal>
  );
}

export default FormModal;
